import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import back_app from '../../lib/back_app';
import { useAuthStore } from '../../store/authStore';

type CreatorInfo = {
  username: string;
};

export default function CreatorHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: auth0_logout } = useAuth0();
  const { user } = useAuthStore();
  const [creatorInfo, setCreatorInfo] = useState<CreatorInfo>({
    username: 'Creator'
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const orgs = user?.orgs || {};
  const org_ids = Object.keys(orgs);
  const has_multi_orgs = org_ids.length > 1;
  const current_org_id = user?.org_id_select || user?.org_id || '';
  const display_name = orgs[current_org_id]?.lbl || creatorInfo.username;
  const logo_url = orgs[current_org_id]?.logo_url;

  const switchOrg = async (org_id: string) => {
    if (switching || org_id == current_org_id)
      return;
    setSwitching(true);
    try {
      const res = await back_app.user_set('org_id_select', org_id);
      if (res?.err && res.err != 'org_id_select_already') {
        console.error('switch org failed', res.err);
        setSwitching(false);
        return;
      }
      window.location.reload();
    } catch (err) {
      console.error('switch org failed', err);
      setSwitching(false);
    }
  };

  useEffect(() => {
    fetchCreatorInfo();
  }, []);

  const fetchCreatorInfo = async () => {
    try {
      const result = await back_app.cprofile_get();
      if (!result.err && result) {
        setCreatorInfo({ username: result.username || 'Creator' });
      }
    } catch (error) {
      console.error('Error fetching creator info:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    auth0_logout({logoutParams: {returnTo: window.location.origin}});
  };

  const navItems = [
    { path: '/creator/dashboard', label: 'Dashboard' },
    { path: '/creator/videos', label: 'My Videos' },
    { path: '/creator/campaigns', label: 'Browse Campaigns' },
    { path: '/creator/my-campaigns', label: 'My Campaigns' },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="bg-white border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {}
          <img
            src="/logo_sponly_color.svg"
            alt="Sponly"
            onClick={() => navigate('/creator/dashboard')}
            className="h-[28.16px] sm:h-[31.68px] w-auto cursor-pointer"
          />

          {}
          <nav className="hidden lg:flex gap-6">
            {navItems.map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`font-medium transition-colors whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-blue-600 font-semibold border-b-2 border-blue-600 pb-1'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {}
          <div className="hidden lg:block relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {logo_url ? (
                <img src={logo_url} alt=""
                  className="w-8 h-8 rounded-full object-cover"/>
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] flex items-center justify-center text-white font-bold text-sm">
                  {display_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="text-left hidden xl:block">
                <div className="text-sm font-semibold text-gray-900">
                  {display_name}
                </div>
              </div>
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {dropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={() => setDropdownOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  {has_multi_orgs && (
                    <>
                      <div className="px-4 pt-1 pb-1 text-xs font-semibold text-gray-400
                        uppercase tracking-wide">
                        Switch organization
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {org_ids.map(oid => {
                          const is_current = oid == current_org_id;
                          return (
                            <button
                              key={oid}
                              disabled={switching}
                              onClick={() => switchOrg(oid)}
                              className={`w-full flex items-center justify-between px-4 py-2
                                text-left text-sm hover:bg-gray-50 transition ${
                                  is_current ? 'bg-gray-50 font-medium text-gray-900'
                                    : 'text-gray-700'
                                }`}
                            >
                              <span className="truncate">{orgs[oid].lbl}</span>
                              {is_current && (
                                <svg className="w-4 h-4 text-blue-600 flex-shrink-0"
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round"
                                    strokeWidth={2} d="M5 13l4 4L19 7"/>
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="my-1 border-t border-gray-200"/>
                    </>
                  )}
                  <button
                    onClick={() => {
                      navigate('/creator/profile');
                      setDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Profile</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/creator/org');
                      setDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>Channel Profile</span>
                  </button>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                ></button>
                  
                  <div className="my-1 border-t border-gray-200"></div>
                  
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLogout();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t py-4 space-y-2">
            {}
            <div className="flex items-center gap-3 px-3 py-2 mb-4">
              {logo_url ? (
                <img src={logo_url} alt=""
                  className="w-10 h-10 rounded-full object-cover"/>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#0066FF] to-[#6B4DE6] flex items-center justify-center text-white font-bold">
                  {display_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="font-semibold text-gray-900">
                {creatorInfo.username}
              </div>
            </div>

            {}
            {navItems.map(item => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`w-full text-left px-3 py-3 rounded-lg font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-blue-600 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}

            <div className="my-2 border-t border-gray-200"></div>

            {has_multi_orgs && (
              <>
                <div className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-400
                  uppercase tracking-wide">
                  Switch organization
                </div>
                {org_ids.map(oid => {
                  const is_current = oid == current_org_id;
                  return (
                    <button
                      key={oid}
                      disabled={switching}
                      onClick={() => {
                        switchOrg(oid);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-3 rounded-lg font-medium
                        transition-colors flex items-center justify-between ${
                          is_current ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      <span className="truncate">{orgs[oid].lbl}</span>
                      {is_current && (
                        <svg className="w-5 h-5 flex-shrink-0"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round"
                            strokeWidth={2} d="M5 13l4 4L19 7"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
                <div className="my-2 border-t border-gray-200"></div>
              </>
            )}

            {}
            <button
              onClick={() => handleNavClick('/creator/profile')}
              className="w-full text-left px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>Profile</span>
            </button>

            <button
              onClick={() => handleNavClick('/creator/org')}
              className="w-full text-left px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span>Channel Profile</span>
            </button>
            
            <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
            ></button>
        
           {}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              className="w-full text-left px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 flex items-center gap-3"
            >
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}